import { Nullable } from '@/utils/types';
import { DashboardViewEnum } from '@/bundles/DashboardAnalytics/types';

// Shared primitives for the dashboards URL <-> filter
// helpers. Field-level mapping stays per-bundle (each dashboard has its own filter
// contract); only the value coercion, enum-param, and view helpers are common.

export type IQueryValue = string | (string | null)[] | null | undefined;
export type IQueryRecord = Record<string, IQueryValue>;

export function toArray (value: IQueryValue): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  return value.length > 0 ? value.split(',').filter(Boolean) : [];
}

export function toBoolean (value: IQueryValue): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === 'true';
}

export function toNullableString (value: IQueryValue): Nullable<string> {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

export function toNumberArray (value: IQueryValue): number[] {
  return toArray(value)
    .map(item => Number(item))
    .filter(item => Number.isFinite(item));
}

export function stringArraysEqual (a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

export function numberArraysEqual (a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

// Enum query param: omit the key when the value is the default, else { [key]: value }.
export function enumToQuery<T extends string> (value: T, key: string, defaultValue: T): Record<string, string> {
  return value === defaultValue ? {} : { [key]: value };
}

// Parse a single enum value from a query, falling back when missing or invalid.
export function queryToEnum<T extends string> (
  query: IQueryRecord,
  key: string,
  validValues: readonly string[],
  fallback: T,
): T {
  const raw = query[key];
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string' || !validValues.includes(value)) {
    return fallback;
  }
  return value as T;
}

export function viewToQuery (view: DashboardViewEnum): Record<string, string> {
  return enumToQuery(view, 'view', DashboardViewEnum.analyze);
}

export function queryToView (query: IQueryRecord): DashboardViewEnum {
  return queryToEnum(query, 'view', Object.values(DashboardViewEnum), DashboardViewEnum.analyze);
}
